import { useMutation, useQueryClient } from '@tanstack/react-query'
import { confirmSignUp, autoSignIn } from 'aws-amplify/auth'

import { queries } from 'src/lib/query-keys'
import { sendEvent } from 'src/shared/api/events/send-event'
import { useCreateConsumerAccount } from 'src/shared/features/consumers/api/use-create-consumer-account'
import { useEventStore } from 'src/shared/store/event-store'

export interface ConsumerConfirmSignUpParams {
  email: string
  code: string
  firstName: string
  lastName: string
  phoneNumber?: string
  isUpdate?: boolean
}

export const useConsumerConfirmSignUp = () => {
  const queryClient = useQueryClient()
  const createConsumerAccount = useCreateConsumerAccount()
  const { gclid, fbclid, utmParams } = useEventStore()

  return useMutation({
    mutationFn: async ({
      email,
      code,
      firstName,
      lastName,
      phoneNumber,
      isUpdate = false,
    }: ConsumerConfirmSignUpParams) => {
      // Using email as username for consumer accounts
      const result = await confirmSignUp({
        username: email, // Use email as username for consumers
        confirmationCode: code,
      })

      try {
        const { nextStep } = await autoSignIn()
        if (nextStep?.signInStep === 'DONE') {
          // Register the consumer in the backend
          if (isUpdate) return { ...result }

          const consumer = await createConsumerAccount.mutateAsync({
            firstName,
            lastName,
            primaryEmail: email,
            phoneNumber,
            gclid, // Pass gclid to backend registration if available
            fbclid, // Pass fbclid to backend registration if available
          })

          await queryClient.invalidateQueries({ queryKey: queries.auth._def })
          await queryClient.invalidateQueries({ queryKey: queries.consumers.me.queryKey })

          sendEvent({
            eventType: 'auth_consumer_confirm_signup_success',
            consumerId: consumer.id,
            info: {
              ...(gclid ? { gclid } : {}),
              ...(fbclid ? { fbclid } : {}),
              ...(utmParams?.source ? { utm_source: utmParams.source } : {}),
              ...(utmParams?.medium ? { utm_medium: utmParams.medium } : {}),
              ...(utmParams?.campaign ? { utm_campaign: utmParams.campaign } : {}),
              ...(utmParams?.term ? { utm_term: utmParams.term } : {}),
              ...(utmParams?.content ? { utm_content: utmParams.content } : {}),
              ...(utmParams?.ad ? { utm_ad: utmParams.ad } : {}),
            },
          })
        }
      } catch (error) {
        // Auto sign-in or backend registration failed, user needs to sign in manually
        console.error('Auto sign-in or backend registration failed after confirmation:', error)
        sendEvent({
          eventType: 'auth_consumer_confirm_signup_auto_signin_failed',
          info: {
            email,
            error: error instanceof Error ? error.message : 'Unknown error',
            ...(gclid ? { gclid } : {}),
            ...(fbclid ? { fbclid } : {}),
            ...(utmParams?.source ? { utm_source: utmParams.source } : {}),
            ...(utmParams?.medium ? { utm_medium: utmParams.medium } : {}),
            ...(utmParams?.campaign ? { utm_campaign: utmParams.campaign } : {}),
            ...(utmParams?.term ? { utm_term: utmParams.term } : {}),
            ...(utmParams?.content ? { utm_content: utmParams.content } : {}),
            ...(utmParams?.ad ? { utm_ad: utmParams.ad } : {}),
          },
        })
      }

      return { ...result }
    },
    onError: (error: unknown) => {
      const errorName = error instanceof Error ? error.name : 'unknown'
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      console.error('Confirm signup error:', errorName, errorMessage)

      sendEvent({
        eventType: 'auth_consumer_confirm_signup_error',
        info: {
          error: errorName,
          message: errorMessage,
          ...(gclid ? { gclid } : {}),
          ...(fbclid ? { fbclid } : {}),
          ...(utmParams?.source ? { utm_source: utmParams.source } : {}),
          ...(utmParams?.medium ? { utm_medium: utmParams.medium } : {}),
          ...(utmParams?.campaign ? { utm_campaign: utmParams.campaign } : {}),
          ...(utmParams?.term ? { utm_term: utmParams.term } : {}),
          ...(utmParams?.content ? { utm_content: utmParams.content } : {}),
          ...(utmParams?.ad ? { utm_ad: utmParams.ad } : {}),
        },
      })
    },
  })
}
